import React, { useState } from "react";
import { useLocalStorage } from "sprint-common-web";

import { useFileRecordApi } from "../../../../common/api/master-api/file-records/useFileRecordApi";
import { mapToNoteItem } from "../../../../common/api/master-api/notes/GetNotesBySubjectIdsResponse";
import { useNotesApi } from "../../../../common/api/master-api/notes/useNotesApi";
import { NoteItem } from "../../../../common/components/notes/hooks/state/NoteItem";
import { FileRecordStatus } from "../../../../common/types/FileRecordStatus";
import { FileRecordStatusDetails } from "../../common/types/FileRecordStatusDetails";
import { getDefaultFileRecordListingConfig, FileRecordListingConfig } from "../config/FileRecordListingConfig";
import { FileRecordListingFilters } from "../hooks/useFileRecordListingFilters";
import { mapToFileRecordListingItem, FileRecordListingItem } from "../types/FileRecordListingItem";
import { FileRecordListingContext } from "./FileRecordListingContext";

export type FileRecordListingProviderProps = React.PropsWithChildren<unknown>;

const LISTING_CONFIG_LOCAL_STORAGE_KEY = "file-record-listing-config";

export const FileRecordListingProvider: React.FC<FileRecordListingProviderProps> = (
    props: FileRecordListingProviderProps
) => {
    const { getFileRecordsByQuery } = useFileRecordApi();
    const { getNotesBySubjectIds } = useNotesApi();

    const [items, setItems] = useState<FileRecordListingItem[]>([]);

    const [areNotesLoading, setAreNotesLoading] = useState<boolean>(true);
    const [areFileRecordsLoading, setAreFileRecordsLoading] = useState<boolean>(false);

    const [selectedItems, setSelectedItems] = useState<FileRecordListingItem[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [notes, setNotes] = useState<Record<string, NoteItem[]>>();

    const [listingConfig, saveListingConfig] = useLocalStorage<FileRecordListingConfig>(
        LISTING_CONFIG_LOCAL_STORAGE_KEY,
        getDefaultFileRecordListingConfig()
    );

    const reloadData = async (filters: FileRecordListingFilters): Promise<void> => {
        setAreFileRecordsLoading(true);

        const fileRecordListResponse = await getFileRecordsByQuery({
            page: filters.page,
            pageSize: filters.pageSize,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
            filePath: filters.filePath,
            content: filters.content,
            deliveryRef: filters.deliveryRef,
            deliveryFileName: filters.deliveryFileName,
            statuses: filters.statuses,
            notifications: filters.notifications,
            recordTypes: filters.recordTypes,
            associations: filters.associations,
            trailers: filters.trailers,
            createdDateFrom: filters.createdDateFrom?.toISOString(),
            createdDateTo: filters.createdDateTo?.toISOString()
        });

        const mappedItems = fileRecordListResponse.items.map(mapToFileRecordListingItem);

        setItems(mappedItems);
        setTotalItems(fileRecordListResponse.totalCount);

        setAreFileRecordsLoading(false);

        await reloadNotes(mappedItems);
    };

    const reloadNotes = async (mappedItems: FileRecordListingItem[]) => {
        setAreNotesLoading(true);

        const subjectIds = mappedItems.map((i) => i.id);

        const notesResponse = await getNotesBySubjectIds({
            subjectIds
        });

        const mappedNotes = Object.entries(notesResponse).reduce((acc, [key, value]) => {
            acc[key] = value.map((item) => mapToNoteItem(item, key));

            return acc;
        }, {} as Record<string, NoteItem[]>);

        setNotes(mappedNotes);
        setAreNotesLoading(false);
    };

    const addFileRecordItems = (newItems: FileRecordListingItem[]) => {
        setItems((prevState) => [...newItems, ...prevState]);
    };

    const approveNotifications = (itemId: string) => {
        setItems((prevItems) =>
            prevItems.map((item) =>
                item.id === itemId ? { ...item, isModified: true, isNotificationApproved: true } : item
            )
        );
    };

    const rejectFileRecord = (fileRecordId: string): void => {
        setItems((prevItems) =>
            prevItems.map((item) =>
                item.id === fileRecordId ? { ...item, isModified: true, status: FileRecordStatus.Rejected } : item
            )
        );
    };

    const updateFileRecordStatus = (
        fileRecordId: string,
        status: FileRecordStatus,
        statusDetails?: FileRecordStatusDetails
    ) => {
        setItems((prevItems) =>
            prevItems.map((item) =>
                item.id === fileRecordId ? { ...item, isModified: true, status, statusDetails } : item
            )
        );
    };

    const removeFileRecord = (fileRecordId: string) => {
        setItems((prevItems) => {
            const filteredVersionsItems = prevItems.filter((item) => item.id !== fileRecordId);

            return [...filteredVersionsItems];
        });
    };

    return (
        <FileRecordListingContext.Provider
            value={{
                notes,
                items,
                selectedItems,
                totalItems,
                areNotesLoading,
                reloadData,
                setSelectedItems,
                approveNotifications,
                addFileRecordItems,
                removeFileRecord,
                updateFileRecordStatus,
                rejectFileRecord,
                areFileRecordsLoading,
                listingConfig,
                saveListingConfig
            }}
        >
            {props.children}
        </FileRecordListingContext.Provider>
    );
};
